import { Suspense } from 'react';
import { useParams } from 'react-router-dom';
import {
  AboutImageSection,
  AboutSectionWbk,
  ActionCardCarousel,
  AnchorsCarousel,
  Announcement,
  BenefitsTable,
  BlogSection,
  BracketsOrganization,
  BusinessSection,
  CardsCarousel,
  CardsCarouselMini,
  CardsGrid,
  CardsInfoCarousel,
  CardsSquareGrid,
  CollapsableTextSection,
  ContentfulStories,
  Counter,
  Cta,
  DealsFeature,
  ExploreDealsGrid,
  FAQs,
  FullScreenCarousel,
  FullScreenGeoCarousel,
  GroupTagCarousel,
  GroupsTable,
  HeroCounterSection,
  HeroGrid,
  HeroImage,
  HeroLeftCounterSection,
  HeroLogo,
  HeroSection,
  HtmlSection,
  IconsGrid,
  IconsLargeGrid,
  ImageInfoCarousel,
  InfoModal,
  InstagramCardsCarousel,
  LinkCards,
  MarqueeBanner,
  MasonryCarousel,
  NewsLetterSection,
  PollSection,
  ProductCarousel,
  ProductFocusedCarousel,
  ProductGeoSection,
  ProductGrid,
  ProductListItems,
  ProductListItemsDateCategorized,
  ProductListItemsDateCategorizedOrganization,
  ProductListItemsDateGrouped,
  ProductListItemsOrganization,
  ProductNumberedCarousel,
  ProductTicketsOrganization,
  RectangularCardCarousel,
  RectangularCarousel,
  ResellBanner,
  RoundRobinOrganization,
  ShowcaseCarousel,
  SocialBannerSection,
  SocialCardsSection,
  SponsorList,
  SpotlightCarousel,
  SpotlightGrid,
  StandingsTable,
  StatsGrid,
  TimelineCarousel,
  VideoGrid,
  WhatsNewSection,
  ZoneCardsCarousel,
  ZoneCarousel,
  ZoneGrid,
} from '@wbk/contentful';
import ErrorBoundary from '../error/ErrorBoundary';
import type { WebSection } from '@wbk/contentful/api';

type Props = {
  section: WebSection;
};

const ContentfulWebSection = ({ section }: Props) => {
  const { slug } = useParams<{ slug: string }>();
  // 1. Render based on section type
  switch (section.renderType) {
    case 'advertise':
    case 'business':
      return <BusinessSection section={section} />;
    case 'collapsable-text':
      return <CollapsableTextSection section={section} />;
    case 'hero':
      return <HeroSection section={section} />;
    case 'hero-grid':
      return <HeroGrid section={section} />;
    case 'hero-counter':
      return <HeroCounterSection section={section} />;
    case 'hero-left-counter':
      return <HeroLeftCounterSection section={section} />;
    case 'hero-image':
      return <HeroImage section={section} />;
    case 'hero-logo':
      return <HeroLogo section={section} />;
    case 'recap':
      return null;
    case 'stories':
      return <ContentfulStories section={section} />;
    case 'newsletter':
      return <NewsLetterSection section={section} slug={slug} />;
    case 'wbk-blog':
      return (
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<></>}>
            <BlogSection section={section} />
          </Suspense>
        </ErrorBoundary>
      );
    case 'whats-new':
      return <WhatsNewSection section={section} />;
    case 'showcase-carousel':
      return <ShowcaseCarousel section={section} />;
    case 'announcement':
      return <Announcement section={section} />;
    case 'marquee':
      return <MarqueeBanner section={section} />;
    case 'cta':
      return <Cta section={section} />;
    case 'about':
      return <AboutSectionWbk section={section} />;
    case 'about-image-section':
      return <AboutImageSection section={section} />;
    case 'about-image-section-reverse':
      return <AboutImageSection section={section} />;
    case 'resell-banner':
      return <ResellBanner section={section} />;
    case 'groups-table':
      return <GroupsTable section={section} />;
    case 'faqs':
      return <FAQs section={section} />;
    case 'list-item-organization':
      return <ProductListItemsOrganization section={section} />;
    case 'video':
      return <VideoGrid section={section} />;
    case 'brackets-organization':
      return <BracketsOrganization section={section} />;
    case 'list-item-date-categorized-organization':
      return <ProductListItemsDateCategorizedOrganization section={section} />;
    case 'event-tickets-organization':
      return <ProductTicketsOrganization section={section} />;
    case 'explore-deals-grid':
      return <ExploreDealsGrid section={section} />;
    case 'explore-deals-featured':
      return <DealsFeature section={section} />;
    case 'round-robin':
      return <RoundRobinOrganization section={section} />;
    case 'info-modal':
      return <InfoModal section={section} />;
    case 'html':
      return <HtmlSection section={section} />;
    default:
      break;
  }

  //  2. Render based on content type
  if (!section.contentCollection?.items?.[0]) {
    return null;
  }

  const type = section.contentCollection?.items?.[0]?.__typename;

  switch (type) {
    case 'Zone':
      return section.renderType === 'fullwidth-carousel' ? (
        <FullScreenCarousel section={section} />
      ) : section.renderType === 'fullwidth-geo-carousel' ? (
        <FullScreenGeoCarousel section={section} />
      ) : section.renderType === 'masonry-carousel' ? (
        <MasonryCarousel section={section} />
      ) : section.renderType === 'carousel' ? (
        <ZoneCarousel section={section} />
      ) : section.renderType === 'cards' ? (
        <ZoneCardsCarousel section={section} />
      ) : section.renderType === 'rectangular-carousel' ||
        section.renderType === 'rectangular-carousel-non-overlay' ? (
        <RectangularCarousel section={section} />
      ) : section.renderType === 'category' ? (
        <IconsGrid section={section} />
      ) : section.renderType === 'action-card-carousel' ? (
        <ActionCardCarousel section={section} />
      ) : (
        <ZoneGrid section={section} />
      );

    case 'WebCard':
      return section.renderType === 'fullwidth-carousel' ? (
        <FullScreenCarousel section={section} />
      ) : section.renderType === 'fullwidth-geo-carousel' ? (
        <FullScreenGeoCarousel section={section} />
      ) : section.renderType === 'masonry-carousel' ? (
        <MasonryCarousel section={section} />
      ) : section.renderType === 'carousel' ? (
        <CardsCarousel section={section} />
      ) : section.renderType === 'anchors' || section.renderType === 'anchors-sticky' ? (
        <AnchorsCarousel section={section} />
      ) : section.renderType === 'social' ? (
        <SocialCardsSection section={section} className='container' />
      ) : section.renderType === 'cards' ? (
        <CardsCarouselMini section={section} />
      ) : section.renderType === 'cards-image-info' ? (
        <ImageInfoCarousel section={section} />
      ) : section.renderType === 'spotlight' ? (
        <SpotlightCarousel section={section} />
      ) : section.renderType === 'spotlight-grid' ? (
        <SpotlightGrid section={section} />
      ) : section.renderType === 'rectangular-carousel' ||
        section.renderType === 'rectangular-carousel-non-overlay' ? (
        <RectangularCarousel section={section} />
      ) : section.renderType === 'rectangular-card-carousel-infinite' ||
        section.renderType === 'rectangular-card-carousel-infinite-non-overlay' ? (
        <RectangularCardCarousel section={section} />
      ) : section.renderType === 'instagram-card' ? (
        <InstagramCardsCarousel section={section} />
      ) : section.renderType === 'cards-grid' ? (
        <CardsSquareGrid section={section} />
      ) : section.renderType === 'link-card' ? (
        <LinkCards section={section} />
      ) : section.renderType === 'category' ? (
        <IconsGrid section={section} />
      ) : section.renderType === 'category-large' ? (
        <IconsLargeGrid section={section} />
      ) : section.renderType === 'info-carousel' ? (
        <CardsInfoCarousel section={section} />
      ) : section.renderType === 'stats-grid' ? (
        <StatsGrid section={section} />
      ) : section.renderType === 'timeline' ? (
        <TimelineCarousel section={section} />
      ) : section.renderType === 'benefits' ? (
        <BenefitsTable section={section} />
      ) : section.renderType === 'counter' ? (
        <Counter section={section} />
      ) : section.renderType === 'standings' ? (
        <StandingsTable section={section} />
      ) : (
        <CardsGrid section={section} />
      );

    case 'Product':
      return section.renderType === 'fullwidth-carousel' ? (
        <FullScreenCarousel section={section} />
      ) : section.renderType === 'fullwidth-geo-carousel' ? (
        <FullScreenGeoCarousel section={section} />
      ) : section.renderType === 'masonry-carousel' ? (
        <MasonryCarousel section={section} />
      ) : section.renderType === 'carousel' ? (
        <ProductCarousel section={section} />
      ) : section.renderType === 'focused-carousel' ? (
        <ProductFocusedCarousel section={section} />
      ) : section.renderType === 'numbered-carousel' ? (
        <ProductNumberedCarousel section={section} />
      ) : section.renderType === 'list-item' ? (
        <ProductListItems section={section} />
      ) : section.renderType === 'list-item-date-grouped' ? (
        <ProductListItemsDateGrouped section={section} />
      ) : section.renderType === 'list-item-date-categorized' ? (
        <ProductListItemsDateCategorized section={section} />
      ) : section.renderType === 'category' ? (
        <IconsGrid section={section} />
      ) : section.renderType === 'geo-grid' ? (
        <ProductGeoSection section={section} />
      ) : section.renderType === 'group-tag-carousel' ? (
        <GroupTagCarousel section={section} />
      ) : section.renderType === 'action-card-carousel' ? (
        <ActionCardCarousel section={section} />
      ) : (
        <ProductGrid section={section} />
      );

    case 'Category':
    case 'Tag':
      return section.renderType === 'rectangular-carousel' ||
        section.renderType === 'rectangular-carousel-non-overlay' ? (
        <RectangularCarousel section={section} />
      ) : (
        <IconsGrid section={section} />
      );
    case 'SponsorList':
      return <SponsorList section={section} />;
    case 'Poll':
      return <PollSection section={section} />;
    case 'SocialBanner':
      return <SocialBannerSection section={section} />;
    default:
      return null;
  }
};

export default ContentfulWebSection;
